import { Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { find } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import {
  DisplayTable,
  StyledTableCell,
} from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { diagnostics } from '@/utils/diagnostics';
import { formatCurrency } from '@/utils/formatting/currency';

import { HypotheticalSaleLoanFormShape } from '../../EstateWaterfallHypotheticalSaleLoanModal.types';
import { useSaleLoanFormContext } from '../../Form/context/SaleLoanForm.context';
import { SaleLoanIllustration_EstateWaterfallFragment } from '../graphql/SaleLoanIllustration.generated';
import { PROJECTION_PSEUDO_ID } from '../SaleLoanIllustrationContent.utils';
import { getYearEventDescription } from './SaleLoanIllustrationDetails.utils';

interface GetProjectedGrowthParams {
  termLength: number | null;
  termStartYear: number | null;
  isTwoGrantorHousehold: boolean;
  recipientId: string;
}

export interface GrowthRow {
  year: number;
  valueAtYearStart: Decimal;
  growth: Decimal;
  noteInterest: Decimal;
  notePrincipal: Decimal;
  valueAtYearEnd: Decimal;
  yearEventDescription?: string;
}

function getProjectedGrowth(
  waterfall: SaleLoanIllustration_EstateWaterfallFragment | null,
  {
    termLength,
    recipientId,
    termStartYear,
    isTwoGrantorHousehold,
  }: GetProjectedGrowthParams
): GrowthRow[] {
  if (!waterfall || !termLength) {
    return [];
  }

  const recipientVizNode = find(
    waterfall.visualizationWithSaleLoanProjections.nodes,
    (n) => n.id === recipientId
  );
  if (!recipientVizNode) {
    diagnostics.error(
      'Recipient not found in waterfall',
      new Error('Recipient not found in waterfall')
    );
    return [];
  }

  const loan = find(
    waterfall.visualizationWithSaleLoanProjections.loans,
    (l) => l.pseudoID === PROJECTION_PSEUDO_ID
  );
  if (!loan) {
    diagnostics.error(
      'Projected loan not found in waterfall',
      new Error('Projected loan not found in waterfall')
    );
    return [];
  }

  const yearDescriptionParams =
    termStartYear && termLength
      ? {
          termStartYear: termStartYear,
          termLengthYears: termLength,
          isTwoGrantorHousehold: isTwoGrantorHousehold,
        }
      : null;

  return recipientVizNode.valueHistory.map((vh) => {
    const loanValueForYear = loan.valueHistory.find(
      (lvh) => lvh.year === vh.year
    );

    return {
      year: vh.year,
      valueAtYearStart: vh.beginningOfYearValue ?? new Decimal(0),
      growth: vh.assetGrowth ?? new Decimal(0),
      valueAtYearEnd: vh.endOfYearValue ?? new Decimal(0),

      // the entity projections will extend out until the second death, whereas
      // the loan projections will only extend out until the term length, so we
      // expect loanValueForYear to be undefined for all years after the term
      // length
      noteInterest:
        loanValueForYear?.interestPaid?.times(new Decimal(-1)) ??
        new Decimal(0),
      notePrincipal:
        loanValueForYear?.principalPaid?.times(new Decimal(-1)) ??
        new Decimal(0),
      yearEventDescription: getYearEventDescription(
        vh.year,
        waterfall,
        yearDescriptionParams
      ),
    };
  });
}

export interface SaleLoanIllustrationGrowthScheduleProps {
  waterfall: SaleLoanIllustration_EstateWaterfallFragment | null;
}

export function SaleLoanIllustrationGrowthSchedule({
  waterfall: illustrationWaterfall,
}: SaleLoanIllustrationGrowthScheduleProps) {
  // this is the waterfall from the root query; we use it here instead of the illustration waterfall
  // so that we can use the display name in some messaging. we shouldn't use it for anything that we
  // expect to change as part of the illustration
  const { waterfall: baseWaterfall } = useSaleLoanFormContext();
  const { isTwoClientHousehold } = useHouseholdDetailsContext();
  const { control } = useFormContext<HypotheticalSaleLoanFormShape>();
  const [termLength, startDate] = useWatch({
    control,
    name: ['termLength', 'startDate'],
  });
  const recipientId = useWatch({
    control,
    name: 'recipientId',
  });

  const growthProjections = useMemo(
    () =>
      getProjectedGrowth(illustrationWaterfall, {
        termLength: Number(termLength || '0'),
        recipientId,
        termStartYear: startDate?.getFullYear() ?? null,
        isTwoGrantorHousehold: isTwoClientHousehold,
      }),
    [
      termLength,
      illustrationWaterfall,
      recipientId,
      startDate,
      isTwoClientHousehold,
    ]
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body-xs">
        Projected growth modeled below takes into account all details from the{' '}
        <strong>{baseWaterfall?.displayName ?? 'modeled'}</strong> waterfall
        including death events, assumptions, cash flows and transfers.
      </Typography>
      <DisplayTable
        columns={[
          { headerName: 'Year', width: '12%' },
          {
            headerName: 'Assets at year start',
            width: '20%',
            align: 'right',
          },
          { headerName: 'Returns', width: '16%', align: 'right' },
          { headerName: 'Note interest', width: '16%', align: 'right' },
          { headerName: 'Note principal', width: '16%', align: 'right' },
          {
            headerName: 'Assets at year end',
            width: '20%',
            align: 'right',
          },
        ]}
      >
        {growthProjections.map((row) => (
          <StyledTableRow key={row.year} sx={{ height: 69 }}>
            <StyledTableCell>
              <Stack>
                <Typography variant="body-xs">{row.year}</Typography>
                {row.yearEventDescription && (
                  <Typography variant="body-xs">
                    {row.yearEventDescription}
                  </Typography>
                )}
              </Stack>
            </StyledTableCell>
            <StyledTableCell align="right">
              <Typography variant="body-xs">
                {formatCurrency(row.valueAtYearStart)}
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="right">
              <Typography
                variant="body-xs"
                color={row.growth.lt(0) ? 'error.main' : undefined}
              >
                {formatCurrency(row.growth, {
                  currencySign: 'accounting',
                })}
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="right">
              <Typography
                variant="body-xs"
                color={row.noteInterest.lt(0) ? 'error.main' : undefined}
              >
                {formatCurrency(row.noteInterest, {
                  currencySign: 'accounting',
                })}
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="right">
              <Typography
                variant="body-xs"
                color={row.notePrincipal.lt(0) ? 'error.main' : undefined}
              >
                {formatCurrency(row.notePrincipal, {
                  currencySign: 'accounting',
                })}
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="right">
              <Typography variant="body-xs">
                {formatCurrency(row.valueAtYearEnd)}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
        ))}
        {growthProjections.length === 0 && (
          <StyledTableRow>
            <StyledTableCell colSpan={5}>
              <Typography variant="body-xs">
                Growth projections will generate when all required fields are
                specified
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
        )}
      </DisplayTable>
    </Stack>
  );
}
